import { ApiKey } from '@/components/api-keys/ApiKeysList';
import { apiJson } from '@/lib/http';

// Use shared helper
const apiCall = <T = unknown>(endpoint: string, options: RequestInit = {}) => apiJson<T>(endpoint, options);

// Backend API response types
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiKeyDto {
  id: string;
  name: string;
  token: string;
  keyPrefix: string;
  scopes: string[];
  environment: string;
  role: string;
  status: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  ttlDays: number | null;
}

interface CreateApiKeyRequest {
  name: string;
  scopes: string[];
  environment: string;
  role: string;
  expiresAt: string | null;
  ttlDays: number;
}

// Helper to convert backend DTO to frontend ApiKey interface
const mapApiKeyDtoToApiKey = (dto: ApiKeyDto): ApiKey => {
  return {
    id: dto.id,
    name: dto.name,
    token: dto.token || dto.keyPrefix, // Use keyPrefix for display if token is not provided
    createdAt: dto.createdAt, // Keep as ISO string, frontend will parse it
    status: dto.status as 'active' | 'revoked' | 'expired',
    scopes: dto.scopes,
    environment: dto.environment,
    expiresAt: dto.expiresAt, // Keep as ISO string or null
    role: dto.role,
    ttlDays: dto.ttlDays,
  };
};

export const apiKeyService = {
  // Get all API keys for the authenticated user
  async getApiKeys(): Promise<ApiKey[]> {
    try {
      const response: ApiResponse<ApiKeyDto[]> = await apiCall('/ApiKeys');
      return response.data.map(mapApiKeyDtoToApiKey);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      throw error;
    }
  },

  // Create a new API key
  async createApiKey(
    name: string,
    scopes: string[],
    environment: string,
    expiresAt: string | null,
    role: string,
    ttlDays: number
  ): Promise<ApiKey> {
    try {
      const request: CreateApiKeyRequest = {
        name,
        scopes,
        environment,
        role,
        expiresAt,
        ttlDays,
      };

      const response: ApiResponse<ApiKeyDto> = await apiCall('/ApiKeys', {
        method: 'POST',
        body: JSON.stringify(request),
      });

      return mapApiKeyDtoToApiKey(response.data);
    } catch (error) {
      console.error('Error creating API key:', error);
      throw error;
    }
  },

  // Revoke an API key
  async revokeApiKey(id: string): Promise<void> {
    try {
      await apiCall(`/ApiKeys/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error revoking API key:', error);
      throw error;
    }
  },

  // Get a specific API key by ID
  async getApiKey(id: string): Promise<ApiKey> {
    try {
      const response: ApiResponse<ApiKeyDto> = await apiCall(`/ApiKeys/${id}`);
      return mapApiKeyDtoToApiKey(response.data);
    } catch (error) {
      console.error('Error fetching API key:', error);
      throw error;
    }
  },

  // Update an API key
  async updateApiKey(
    id: string,
    updates: Partial<{
      name: string;
      scopes: string[];
      environment: string;
      role: string;
      expiresAt: string | null;
    }>
  ): Promise<ApiKey> {
    try {
      const response: ApiResponse<ApiKeyDto> = await apiCall(`/ApiKeys/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      return mapApiKeyDtoToApiKey(response.data);
    } catch (error) {
      console.error('Error updating API key:', error);
      throw error;
    }
  },
}; 